import React, { useContext, useState, useEffect } from "react";
import axios from "../../axiosConfig";
import TrainerLayout from "./TrainerLayout";
import { TrainerContext } from "../../context/TrainerContext";
import { FiPlus, FiTrash2, FiUsers, FiSearch, FiChevronLeft } from "react-icons/fi";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { useNavigate } from "react-router-dom";
import "../../styles/TrainerGroups.css";

const TrainerGroups = () => {
    const { trainer } = useContext(TrainerContext);
    const navigate = useNavigate();

    // Groups list state
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Create group modal state (multi-step)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [modalStep, setModalStep] = useState("form"); // "form", "selectTrainers", "selectDogs"
    const [groupName, setGroupName] = useState("");
    const [groupDescription, setGroupDescription] = useState("");
    const [maxCapacity, setMaxCapacity] = useState(10);
    const [selectedTrainers, setSelectedTrainers] = useState([]);
    const [selectedDogs, setSelectedDogs] = useState([]);
    const [modalTrainerSearch, setModalTrainerSearch] = useState("");
    const [modalDogSearch, setModalDogSearch] = useState("");
    const [modalTrainers, setModalTrainers] = useState([]);
    const [modalDogs, setModalDogs] = useState([]);

    // Delete modal state for groups
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);

    // Mobile responsive styling - matching TrainerTrainings but with more left space
    const getContainerStyle = () => {
        const isMobile = window.innerWidth <= 600; // Changed to 600px to catch more mobile devices

        if (isMobile) {
            return {
                padding: '20px 20px', // Equal padding on mobile
                marginLeft: '20px',   // Equal margins on mobile
                marginRight: '20px',  // Equal margins on mobile
                width: 'calc(100% - 40px)', // Balanced width calculation
                maxWidth: 'none',
                boxSizing: 'border-box',
                backgroundColor: '#f9fafb',
                minHeight: '100vh'
            };
        } else {
            return {
                padding: '20px 10px',
                marginLeft: '80px',  // Increased from 40px to give more space from sidebar
                marginRight: '10px',
                width: 'calc(100% - 90px)', // Adjusted width calculation
                maxWidth: 'none',
                boxSizing: 'border-box',
                backgroundColor: '#f9fafb',
                minHeight: '100vh'
            };
        }
    };

    // Fetch groups for the school
    const fetchGroups = () => {
        if (!trainer?.schoolId) return;
        const token = localStorage.getItem("jwt");
        axios
            .get(`/api/v1/groups/school/${trainer.schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                setGroups(res.data || []);
                setLoading(false);
            })
            .catch(() => {
                setError("Nie udało się pobrać grup.");
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchGroups();
    }, [trainer]);

    // Fetch trainers for the modal selection step
    useEffect(() => {
        if (!trainer?.schoolId) return;
        const token = localStorage.getItem("jwt");
        axios
            .get(`/api/v1/trainer/find-all/${trainer.schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                setModalTrainers(res.data || []);
            })
            .catch(() => {
                // Optionally handle error
            });
    }, [trainer]);

    // Fetch dogs for booking for the modal selection step
    const fetchDogs = () => {
        if (!trainer?.schoolId) return;
        const token = localStorage.getItem("jwt");
        axios
            .get(`/api/v1/dog/list/${trainer.schoolId}/booking`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                setModalDogs(res.data || []);
            })
            .catch(() => {
                // Optionally handle error
            });
    };

    // Handlers to change modal step
    const goToStep = (step) => {
        if (step === "selectDogs") {
            fetchDogs();
        }
        setModalStep(step);
    };

    // Handle final group creation (POST with all data)
    const handleCreateGroup = () => {
        if (!groupName.trim()) return;
        const token = localStorage.getItem("jwt");
        const payload = {
            name: groupName,
            description: groupDescription,
            schoolId: trainer.schoolId,
            dogIds: selectedDogs.length > 0 ? selectedDogs : null,
            trainerIds: selectedTrainers.length > 0 ? selectedTrainers : null,
            maxCapacity: maxCapacity || 10
        };

        axios
            .post("/api/v1/groups", payload, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                // Success notification
                showNotification("Grupa utworzona pomyślnie", "success");

                // Reset modal state
                setIsCreateModalOpen(false);
                setModalStep("form");
                setGroupName("");
                setGroupDescription("");
                setSelectedTrainers([]);
                setSelectedDogs([]);
                setMaxCapacity(10);
                fetchGroups();
            })
            .catch((err) => {
                const errorMsg = err.response?.data?.message || "Błąd przy tworzeniu grupy";
                showNotification(errorMsg, "error");
            });
    };

    // Helper to show notifications
    const showNotification = (message, type = "success") => {
        const notification = document.createElement("div");
        notification.className = type === "success" ? "success-notification" : "error-notification";
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => {
            document.body.removeChild(notification);
        }, 3000);
    };

    // Handle deletion of a group
    const handleDeleteGroup = (groupId) => {
        const token = localStorage.getItem("jwt");
        axios
            .delete(`/api/v1/groups/${groupId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                showNotification("Grupa usunięta pomyślnie", "success");
                setIsDeleteModalOpen(false);
                setGroupToDelete(null);
                fetchGroups();
            })
            .catch((err) => {
                // Check if error is due to future trainings
                const errorMsg = err.response?.data?.message || "Błąd przy usuwaniu grupy";
                showNotification(errorMsg, "error");
                setIsDeleteModalOpen(false);
                setGroupToDelete(null);
            });
    };

    // Filter modal trainers by search term
    const filteredModalTrainers = modalTrainers.filter((t) => {
        const term = modalTrainerSearch.toLowerCase();
        const fullName = `${t.firstName} ${t.lastName}`.toLowerCase();
        return fullName.includes(term);
    });

    // Filter modal dogs by search term
    const filteredModalDogs = modalDogs.filter((d) => {
        const term = modalDogSearch.toLowerCase();
        const info = `${d.name} ${d.breed} ${d.ownersName}`.toLowerCase();
        return info.includes(term);
    });

    return (
        <TrainerLayout>
            <div className="trainer-groups-responsive-container">
                <div className="modern-trainer-groups-content">
                    <div className="modern-header">
                        <h2 className="modern-title">Grupy</h2>
                        <button
                            className="modern-create-btn"
                            onClick={() => {
                                setIsCreateModalOpen(true);
                                setModalStep("form");
                            }}
                        >
                            <FiPlus className="btn-icon" />
                            <span>Utwórz grupę</span>
                        </button>
                    </div>

                    {error && <div className="modern-error">{error}</div>}
                    {loading ? (
                        <div className="modern-loading">
                            <div className="modern-spinner"></div>
                            <p>Ładowanie...</p>
                        </div>
                    ) : (
                        <div className="modern-groups-grid">
                            {groups.length === 0 ? (
                                <div className="modern-empty-state">
                                    <FiUsers className="empty-icon" />
                                    <p>Brak grup w szkole</p>
                                    <button
                                        className="modern-create-btn"
                                        onClick={() => {
                                            setIsCreateModalOpen(true);
                                            setModalStep("form");
                                        }}
                                    >
                                        <FiPlus className="btn-icon" />
                                        <span>Utwórz pierwszą grupę</span>
                                    </button>
                                </div>
                            ) : (
                                groups.map((g) => (
                                    <div
                                        key={g.id}
                                        className="modern-group-card"
                                    >
                                        <div
                                            className="card-content"
                                            onClick={() => navigate(`/trainer/group/${g.id}`)}
                                        >
                                            <h4 className="card-title">{g.name}</h4>
                                            <p className="card-description">{g.description}</p>

                                            <div className="card-info">
                                                <span className="card-count">
                                                    {g.trainersCount || 0} trenerów
                                                </span>
                                                <span className="card-count">
                                                    {g.dogsCount || 0}/{g.maxCapacity || 10} pupili
                                                </span>
                                            </div>
                                        </div>

                                        <div className="card-footer">
                                            <button
                                                className="modern-delete-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setGroupToDelete(g);
                                                    setIsDeleteModalOpen(true);
                                                }}
                                                title="Usuń grupę"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* Multi-step Create Group Modal */}
                    {isCreateModalOpen && (
                        <div className="modern-modal-overlay">
                            <div className="modern-modal">
                                <div className="modern-modal-header">
                                    {modalStep !== "form" && (
                                        <button
                                            className="modern-back-btn"
                                            onClick={() => goToStep("form")}
                                        >
                                            <FiChevronLeft />
                                        </button>
                                    )}
                                    <h2 className="modern-modal-title">
                                        {modalStep === "form" && "Utwórz grupę"}
                                        {modalStep === "selectTrainers" && "Wybierz trenerów"}
                                        {modalStep === "selectDogs" && "Wybierz pupile"}
                                    </h2>
                                    <button
                                        className="modern-close-btn"
                                        onClick={() => setIsCreateModalOpen(false)}
                                    >
                                        &times;
                                    </button>
                                </div>

                                <div className="modern-modal-content">
                                    {modalStep === "form" && (
                                        <>
                                            <div className="modern-input-group">
                                                <label htmlFor="groupName">Nazwa grupy</label>
                                                <input
                                                    id="groupName"
                                                    type="text"
                                                    value={groupName}
                                                    onChange={(e) => setGroupName(e.target.value)}
                                                    placeholder="Podaj nazwę grupy"
                                                />
                                            </div>
                                            <div className="modern-input-group">
                                                <label htmlFor="groupDesc">Opis grupy</label>
                                                <textarea
                                                    id="groupDesc"
                                                    value={groupDescription}
                                                    onChange={(e) => setGroupDescription(e.target.value)}
                                                    placeholder="Podaj opis grupy (opcjonalnie)"
                                                    rows="4"
                                                />
                                            </div>

                                            <div className="modern-input-group">
                                                <label htmlFor="maxCapacity">Maksymalna liczba pupili</label>
                                                <input
                                                    id="maxCapacity"
                                                    type="number"
                                                    min="1"
                                                    value={maxCapacity}
                                                    onChange={(e) => setMaxCapacity(parseInt(e.target.value) || 10)}
                                                    placeholder="10"
                                                />
                                            </div>

                                            {selectedTrainers.length > 0 && (
                                                <div className="modern-selection-summary">
                                                    <h4>Wybrani trenerzy ({selectedTrainers.length})</h4>
                                                    <button
                                                        className="modern-edit-selection"
                                                        onClick={() => goToStep("selectTrainers")}
                                                    >
                                                        Edytuj
                                                    </button>
                                                </div>
                                            )}

                                            {selectedDogs.length > 0 && (
                                                <div className="modern-selection-summary">
                                                    <h4>Wybrani pupile ({selectedDogs.length})</h4>
                                                    <button
                                                        className="modern-edit-selection"
                                                        onClick={() => goToStep("selectDogs")}
                                                    >
                                                        Edytuj
                                                    </button>
                                                </div>
                                            )}

                                            <div className="modern-selection-actions">
                                                <button
                                                    className="modern-selection-btn"
                                                    onClick={() => goToStep("selectTrainers")}
                                                >
                                                    <FiPlus className="btn-icon" />
                                                    <span>Dodaj trenera</span>
                                                </button>
                                                <button
                                                    className="modern-selection-btn"
                                                    onClick={() => goToStep("selectDogs")}
                                                >
                                                    <FiPlus className="btn-icon" />
                                                    <span>Dodaj pupila</span>
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    {modalStep === "selectTrainers" && (
                                        <>
                                            <div className="modern-search-box">
                                                <FiSearch className="search-icon" />
                                                <input
                                                    type="text"
                                                    placeholder="Wyszukaj trenera..."
                                                    value={modalTrainerSearch}
                                                    onChange={(e) => setModalTrainerSearch(e.target.value)}
                                                />
                                            </div>
                                            <div className="modern-selection-list">
                                                {filteredModalTrainers.length === 0 ? (
                                                    <p className="modern-empty-selection">Brak trenerów.</p>
                                                ) : (
                                                    filteredModalTrainers.map((t) => (
                                                        <div key={t.id} className="modern-selection-item">
                                                            <label className="modern-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedTrainers.includes(t.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedTrainers([...selectedTrainers, t.id]);
                                                                        } else {
                                                                            setSelectedTrainers(selectedTrainers.filter((id) => id !== t.id));
                                                                        }
                                                                    }}
                                                                />
                                                                <span className="checkmark"></span>
                                                                <span className="name">{t.firstName} {t.lastName}</span>
                                                            </label>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {modalStep === "selectDogs" && (
                                        <>
                                            <div className="modern-search-box">
                                                <FiSearch className="search-icon" />
                                                <input
                                                    type="text"
                                                    placeholder="Wyszukaj pupila..."
                                                    value={modalDogSearch}
                                                    onChange={(e) => setModalDogSearch(e.target.value)}
                                                />
                                            </div>
                                            <div className="modern-selection-list">
                                                {filteredModalDogs.length === 0 ? (
                                                    <p className="modern-empty-selection">Brak pupili.</p>
                                                ) : (
                                                    filteredModalDogs.map((d) => (
                                                        <div key={d.id} className="modern-selection-item">
                                                            <label className="modern-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedDogs.includes(d.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedDogs([...selectedDogs, d.id]);
                                                                        } else {
                                                                            setSelectedDogs(selectedDogs.filter((id) => id !== d.id));
                                                                        }
                                                                    }}
                                                                />
                                                                <span className="checkmark"></span>
                                                                <span className="name">
                                                                    {d.name} {d.breed && `(${d.breed})`}
                                                                    {d.ownersName && <span className="owner"> - {d.ownersName}</span>}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="modern-modal-footer">
                                    {modalStep === "form" ? (
                                        <>
                                            <button
                                                className="modern-cancel-btn"
                                                onClick={() => setIsCreateModalOpen(false)}
                                            >
                                                Anuluj
                                            </button>
                                            <button
                                                className="modern-create-btn"
                                                onClick={handleCreateGroup}
                                                disabled={!groupName.trim()}
                                            >
                                                Utwórz grupę
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            className="modern-save-btn"
                                            onClick={() => goToStep("form")}
                                        >
                                            Zatwierdź wybór
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    {isDeleteModalOpen && groupToDelete && (
                        <DeleteConfirmationModal
                            isOpen={isDeleteModalOpen}
                            onClose={() => {
                                setIsDeleteModalOpen(false);
                                setGroupToDelete(null);
                            }}
                            onConfirm={() => handleDeleteGroup(groupToDelete.id)}
                            itemName={`Grupa "${groupToDelete.name}"`}
                        />
                    )}
                </div>
            </div>
        </TrainerLayout>
    );
};

export default TrainerGroups;